import React, { useState, useRef } from 'react';
import { ExternalLink, Upload, Link2, Loader2, Trash2, Bookmark } from "lucide-react";
import PageHeader from './PageHeader';
import PaletteDisplay from './PaletteDisplay';
import CompactExportDropdown from './CompactExportDropdown';

const ImageExtractor = ({
    selectedColor,
    onColorClick,
    visibleFields = [],
    colorBlindnessType = 'none',
    isDark = false,
    addColorToPalette,
    setVisualizerColors,
    savePalette,
    extractedPalette = [],
    setExtractedPalette,
    addColorToCustomPalette
}) => {
    const [imageUrl, setImageUrl] = useState('');
    const [isExtracting, setIsExtracting] = useState(false);
    const [extractionError, setExtractionError] = useState('');
    const fileInputRef = useRef(null);

    // Image extraction functions
    const extractColorsFromImage = async (imageSrc) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';

            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                // Set canvas size to image size (but limit to prevent memory issues)
                const maxSize = 400;
                let { width, height } = img;

                if (width > maxSize || height > maxSize) {
                    if (width > height) {
                        height = (height / width) * maxSize;
                        width = maxSize;
                    } else {
                        width = (width / height) * maxSize;
                        height = maxSize;
                    }
                }

                canvas.width = width;
                canvas.height = height;

                // Draw image to canvas
                ctx.drawImage(img, 0, 0, width, height);

                try {
                    // Get image data
                    const imageData = ctx.getImageData(0, 0, width, height);
                    const colors = extractDominantColors(imageData.data, 12);
                    resolve(colors);
                } catch (error) {
                    reject(new Error('Failed to process image. This might be due to CORS restrictions.'));
                }
            };

            img.onerror = () => {
                reject(new Error('Failed to load image. Please check the URL or try a different image.'));
            };

            img.src = imageSrc;
        });
    };

    // Color quantization algorithm to extract dominant colors
    const extractDominantColors = (imageData, numColors) => {
        const pixels = [];

        // Sample pixels (take every 10th pixel for performance)
        for (let i = 0; i < imageData.length; i += 40) { // RGBA = 4 bytes per pixel, so 40 = every 10th pixel
            const r = imageData[i];
            const g = imageData[i + 1];
            const b = imageData[i + 2];
            const a = imageData[i + 3];

            // Skip transparent pixels and very light/dark pixels
            if (a > 128 && !(r > 240 && g > 240 && b > 240) && !(r < 15 && g < 15 && b < 15)) {
                pixels.push([r, g, b]);
            }
        }

        if (pixels.length === 0) {
            return ['#000000']; // Fallback if no valid pixels
        }

        // Simple k-means clustering for color quantization
        const clusters = kMeansColors(pixels, numColors);

        return clusters.map(cluster => {
            const [r, g, b] = cluster.map(c => Math.round(c));
            return rgbToHex(r, g, b);
        });
    };

    // Simple k-means clustering implementation
    const kMeansColors = (pixels, k) => {
        if (pixels.length === 0) return [];
        if (k >= pixels.length) return pixels.map(p => [...p]);

        // Initialize centroids randomly
        let centroids = [];
        for (let i = 0; i < k; i++) {
            centroids.push([...pixels[Math.floor(Math.random() * pixels.length)]]);
        }

        // Run k-means for a few iterations
        for (let iteration = 0; iteration < 10; iteration++) {
            const clusters = Array(k).fill().map(() => []);

            // Assign pixels to nearest centroid
            pixels.forEach(pixel => {
                let minDistance = Infinity;
                let nearestCluster = 0;

                // Fix: Create a stable reference to centroids for this iteration
                const currentCentroids = centroids;
                currentCentroids.forEach((centroid, index) => {
                    const distance = Math.sqrt(
                        Math.pow(pixel[0] - centroid[0], 2) +
                        Math.pow(pixel[1] - centroid[1], 2) +
                        Math.pow(pixel[2] - centroid[2], 2)
                    );

                    if (distance < minDistance) {
                        minDistance = distance;
                        nearestCluster = index;
                    }
                });

                clusters[nearestCluster].push(pixel);
            });

            // Update centroids
            const newCentroids = clusters.map((cluster, clusterIndex) => {
                if (cluster.length === 0) return centroids[clusterIndex];

                const sum = cluster.reduce(
                    (acc, pixel) => [acc[0] + pixel[0], acc[1] + pixel[1], acc[2] + pixel[2]],
                    [0, 0, 0]
                );

                return [sum[0] / cluster.length, sum[1] / cluster.length, sum[2] / cluster.length];
            });

            centroids = newCentroids;
        }

        return centroids.filter(centroid =>
            !isNaN(centroid[0]) && !isNaN(centroid[1]) && !isNaN(centroid[2])
        );
    };

    const rgbToHex = (r, g, b) => {
        return "#" + [r, g, b].map(x => {
            const hex = Math.max(0, Math.min(255, Math.round(x))).toString(16);
            return hex.length === 1 ? "0" + hex : hex;
        }).join("");
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setExtractionError('Please select a valid image file.');
            return;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            setExtractionError('Image file is too large. Please select an image smaller than 10MB.');
            return;
        }

        setIsExtracting(true);
        setExtractionError('');

        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const colors = await extractColorsFromImage(e.target.result);
                    setExtractedPalette(colors);
                } catch (error) {
                    setExtractionError(error.message);
                } finally {
                    setIsExtracting(false);
                }
            };
            reader.readAsDataURL(file);
        } catch (error) {
            setExtractionError('Failed to read the image file.');
            setIsExtracting(false);
        }
    };

    const handleUrlExtraction = async () => {
        if (!imageUrl.trim()) {
            setExtractionError('Please enter a valid image URL.');
            return;
        }

        setIsExtracting(true);
        setExtractionError('');

        try {
            const colors = await extractColorsFromImage(imageUrl);
            setExtractedPalette(colors);
        } catch (error) {
            setExtractionError(error.message);
        } finally {
            setIsExtracting(false);
        }
    };

    const clearExtractedPalette = () => {
        setExtractedPalette([]);
        setImageUrl('');
        setExtractionError('');
    };

    const handleExportToVisualizer = (colors, paletteName, event) => {
        const colorObjects = colors.map((color, index) => ({
            id: `${paletteName.toLowerCase().replace(/\s+/g, '-')}-${index}`,
            value: color
        }));

        setVisualizerColors(colorObjects, event.target);
    };

    // Generate CSS for palette
    const generatePaletteCss = (colors, paletteName) => {
        const cssVarName = paletteName.toLowerCase().replace(/\s+/g, '-');
        return `:root {\n${colors.map((color, i) => `  --${cssVarName}-${i + 1}: ${color};`).join('\n')}\n}\n\n/* Usage examples */\n.${cssVarName}-primary { color: var(--${cssVarName}-1); }\n.${cssVarName}-bg { background-color: var(--${cssVarName}-1); }`;
    };

    // Generate SVG for palette
    const generatePaletteSvg = (colors, paletteName) => {
        const width = colors.length * 60;
        const height = 80;

        return `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
  <!-- ${paletteName} Palette -->
  <rect width="${width}" height="${height}" fill="white" stroke="#e5e7eb" stroke-width="1" rx="8"/>
  ${colors.map((color, i) => `
  <rect x="${i * 60 + 10}" y="10" width="50" height="50" fill="${color}" stroke="#fff" stroke-width="2" rx="4"/>
  <text x="${i * 60 + 35}" y="72" text-anchor="middle" font-family="Arial, sans-serif" font-size="10" fill="#666">${color}</text>`).join('')}
</svg>`;
    };

    return (
        <div className="overflow-visible">
            <PageHeader
                title="Image Extractor"
                description="Extract color palettes from images"
            />
            <div className="mb-6" />

            {!selectedColor ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <div className="mb-4">
                        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Upload className="w-8 h-8" />
                        </div>
                        <h3 className="text-lg font-medium mb-2">Start Extracting</h3>
                        <p className="text-sm">Select a base color from the toolbar, then upload an image or paste a URL to extract its color palette.</p>
                    </div>
                </div>
            ) : (
                <div className="space-y-6 overflow-visible">
                    {/* Image Upload Section */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                Extract Colors from Image
                            </h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Upload an image file or provide a URL to extract colors
                            </p>
                        </div>
                        <div className="p-4">
                            {/* File Upload */}
                            <div className="mb-6">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 block uppercase tracking-wide">
                                Upload Image File
                            </label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileUpload}
                                    accept="image/*"
                                    className="hidden"
                                />
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isExtracting}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                                >
                                    <Upload className="w-4 h-4" />
                                    Choose Image
                                </button>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    JPG, PNG, GIF up to 10MB
                                </span>
                            </div>
                        </div>

                        {/* URL Input */}
                        <div className="mb-6">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 block uppercase tracking-wide">
                                Or paste image URL
                            </label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="text"
                                    value={imageUrl}
                                    onChange={(e) => setImageUrl(e.target.value)}
                                    placeholder="https://example.com/image.jpg"
                                    disabled={isExtracting}
                                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                                />
                                <button
                                    onClick={handleUrlExtraction}
                                    disabled={isExtracting || !imageUrl.trim()}
                                    className="flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                                >
                                    {isExtracting ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Link2 className="w-4 h-4" />
                                    )}
                                    Extract
                                </button>
                            </div>
                        </div>

                        {/* Error Display */}
                        {extractionError && (
                            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                                <p className="text-sm text-red-600 dark:text-red-400">
                                    {extractionError}
                                </p>
                            </div>
                        )}

                        {/* Loading State */}
                        {isExtracting && (
                            <div className="text-center py-8">
                                <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500 mb-3" />
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Extracting colors from image...
                                </p>
                            </div>
                        )}
                        </div>
                    </div>

                    {/* Extracted Palette Display */}
                    {extractedPalette.length > 0 && (
                        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                            Extracted Palette
                                        </h4>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            {extractedPalette.length} colors extracted from image
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={clearExtractedPalette}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors duration-200"
                                        >
                                            <Trash2 className="w-3 h-3" />
                                            Clear
                                        </button>
                                        <button
                                            onClick={() => savePalette('Extracted Palette', extractedPalette)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-md hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors duration-200"
                                        >
                                            <Bookmark className="w-3 h-3" />
                                            Save
                                        </button>
                                        <button
                                            onClick={(event) => handleExportToVisualizer(extractedPalette, 'Extracted Palette', event)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors duration-200"
                                        >
                                            <ExternalLink className="w-3 h-3" />
                                            Visualizer
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="p-4">
                                <PaletteDisplay
                                    colors={extractedPalette}
                                    onColorClick={onColorClick}
                                    baseColor={selectedColor}
                                    visibleFields={visibleFields}
                                    colorBlindnessType={colorBlindnessType}
                                    isDark={isDark}
                                    addColorToPalette={addColorToPalette}
                                    addColorToCustomPalette={addColorToCustomPalette}
                                />
                            </div>
                            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
                                <CompactExportDropdown
                                    colors={extractedPalette}
                                    paletteName="Extracted Palette"
                                    getCss={() => generatePaletteCss(extractedPalette, 'Extracted Palette')}
                                    getSvg={() => generatePaletteSvg(extractedPalette, 'Extracted Palette')}
                                />
                            </div>
                        </div>
                    )}

                    {/* Tips for Image Extraction */}
                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                        <h4 className="text-sm font-semibold text-purple-900 dark:text-purple-100 mb-2">
                            🎨 Extraction Tips
                        </h4>
                        <ul className="text-xs text-purple-800 dark:text-purple-200 space-y-1">
                            <li>• High-contrast images work best for color extraction</li>
                            <li>• Images with distinct color regions produce clearer palettes</li>
                            <li>• The algorithm finds the most dominant colors in the image</li>
                            <li>• Try different images to discover unique color combinations</li>
                            <li>• Some images may have CORS restrictions when using URLs</li>
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImageExtractor;
